import React, { useMemo, useState } from 'react';
import { GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { useMapsLoader } from '../../../../components/MapsProvider';
import { DEFAULT_MAP_CENTER } from '../../../../config/maps';
import { useDriverLocationsStore } from '../../../../store/driverLocationsStore';
import { initialsOf, avatarBgFor, relativeTime } from '../liveTrackingTypes';
import styles from './MapView.module.css';

/**
 * Real Google Map showing live driver positions, sourced from
 * `useDriverLocationsStore` (the same store the personnel list reads from).
 *
 * Each marker is a labelled pin colored by the driver's avatar bucket so it
 * matches the corresponding row in `FieldPersonnelList`. Selecting a pin
 * opens an `InfoWindow` with the driver's name, route, last-seen timestamp
 * and reported speed.
 *
 * Falls back to a "Map unavailable" placeholder when the Maps script can't
 * load (missing API key, billing not enabled, etc.) so the rest of the
 * dashboard keeps rendering.
 */

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

export const MapView: React.FC = () => {
  const { isLoaded, loadError, isConfigured } = useMapsLoader();
  const live = useDriverLocationsStore((s) => s.live);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const positions = useMemo(
    () =>
      live
        .filter((l) => Number.isFinite(l.latitude) && Number.isFinite(l.longitude))
        .map((l) => ({
          id: l.id,
          driverId: l.driver_id,
          name: l.driver_name || `Driver #${l.driver_id}`,
          route: l.route_name || '—',
          position: { lat: Number(l.latitude), lng: Number(l.longitude) },
          speed: l.speed_kmh ?? 0,
          capturedAt: l.captured_at,
        })),
    [live]
  );

  // Fit-to-markers center: average of marker positions, falling back to HQ
  // when the live feed is empty.
  const center = useMemo(() => {
    if (positions.length === 0) return DEFAULT_MAP_CENTER;
    const sum = positions.reduce(
      (acc, p) => ({ lat: acc.lat + p.position.lat, lng: acc.lng + p.position.lng }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / positions.length, lng: sum.lng / positions.length };
  }, [positions]);

  // Auto-pick a sensible zoom: a single marker zooms in close, multiple
  // markers zoom out so all fit in view.
  const zoom = positions.length <= 1 ? 12 : 10;

  const fallback = (message: string) => (
    <div className={styles.mapContainer}>
      <div className={styles.refreshChip}>
        <span className={styles.refreshDot} />
        Auto-refresh: 30s
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6B7280',
          fontSize: 13,
          textAlign: 'center',
          padding: 16,
        }}
      >
        {message}
      </div>
    </div>
  );

  if (!isConfigured) {
    return fallback('Map unavailable. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env to enable.');
  }
  if (loadError) {
    return fallback(`Map failed to load: ${loadError.message}`);
  }
  if (!isLoaded) {
    return fallback('Loading map…');
  }

  const selected = positions.find((p) => p.id === selectedId) ?? null;

  return (
    <div className={styles.mapContainer}>
      <div className={styles.refreshChip}>
        <span className={styles.refreshDot} />
        Auto-refresh: 30s
      </div>
      <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={zoom} options={mapOptions}>
        {positions.map((p) => (
          <MarkerF
            key={p.id}
            position={p.position}
            title={p.name}
            label={{
              text: initialsOf(p.name),
              color: '#fff',
              fontSize: '11px',
              fontWeight: '700',
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: avatarBgFor(p.name),
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
              scale: 14,
            }}
            onClick={() => setSelectedId(p.id)}
          />
        ))}
        {selected && (
          <InfoWindowF
            position={selected.position}
            onCloseClick={() => setSelectedId(null)}
          >
            <div style={{ minWidth: 160, fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{selected.name}</div>
              <div>Route: {selected.route}</div>
              <div>Speed: {selected.speed.toFixed(0)} km/h</div>
              <div style={{ color: '#6B7280', marginTop: 4 }}>
                Last seen {relativeTime(selected.capturedAt)}
              </div>
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>
    </div>
  );
};
